// ConversationList.js - With bulk selection
import React, { useState, useCallback, useMemo } from 'react';
import {
    Box,
    VStack,
    Button,
    Image,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useDisclosure,
    IconButton,
    Flex,
    Text,
    Checkbox,
    HStack,
    Badge,
    useColorModeValue,
    Collapse,
    Divider,
} from '@chakra-ui/react';
import { 
    HamburgerIcon, 
    AddIcon, 
    DeleteIcon, 
    CheckIcon,
    CloseIcon 
} from '@chakra-ui/icons';
import { FaEdit } from 'react-icons/fa';
import ConversationItem from './ConversationItem';
import BulkDeleteConfirmationModal from './BulkDeleteConfirmationModal';
import logo from './sldatatech.png';
import aisystems from './sldatatech_aisystems.png';

const ConversationList = ({
    conversations,
    onSelect,
    onNewConversation,
    onDelete,
    onBulkDelete, // New prop
    onRename,
    onMove,
    workspaces,
    currentWorkspaceId,
    currentConversationId
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedConversations, setSelectedConversations] = useState(new Set());
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const selectionBg = useColorModeValue('blue.50', 'blue.900');
    const selectionBorder = useColorModeValue('blue.200', 'blue.700');

    // Filter out temporary conversations for bulk operations
    const selectableConversations = useMemo(() => 
        conversations.filter(conv => !conv.isTemporary), 
        [conversations]
    );

    const selectedCount = selectedConversations.size;
    const allSelectableSelected = selectedCount === selectableConversations.length && selectableConversations.length > 0;

    // Toggle selection mode
    const toggleSelectionMode = useCallback(() => {
        setIsSelectionMode(prev => !prev);
        setSelectedConversations(new Set());
    }, []);

    // Handle individual conversation selection
    const toggleConversationSelection = useCallback((conversationId) => {
        setSelectedConversations(prev => {
            const newSet = new Set(prev);
            if (newSet.has(conversationId)) {
                newSet.delete(conversationId);
            } else {
                newSet.add(conversationId);
            }
            return newSet;
        });
    }, []);

    // Handle select all/none
    const toggleSelectAll = useCallback(() => {
        if (allSelectableSelected) {
            setSelectedConversations(new Set());
        } else {
            setSelectedConversations(new Set(selectableConversations.map(conv => conv.id)));
        }
    }, [allSelectableSelected, selectableConversations]);

    // Handle bulk deletion
    const handleBulkDelete = useCallback(async () => {
        setIsDeleting(true);
        try {
            const conversationIds = Array.from(selectedConversations);
            await onBulkDelete(conversationIds);
            
            // Exit selection mode and clear selections
            setIsSelectionMode(false);
            setSelectedConversations(new Set());
        } catch (error) {
            console.error('Bulk delete failed:', error);
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    }, [selectedConversations, onBulkDelete]);

    // Check if current conversation is selected
    const isCurrentConversationSelected = selectedConversations.has(currentConversationId);

    const conversationsList = (
        <VStack spacing={0.5} align="stretch">
            {/* Selection Mode Header */}
            <Collapse in={isSelectionMode} animateOpacity>
                <Box p={3} bg={selectionBg} borderRadius="md" mb={2}>
                    <VStack spacing={2} align="stretch">
                        <Flex justify="space-between" align="center">
                            <HStack>
                                <Checkbox
                                    isChecked={allSelectableSelected}
                                    isIndeterminate={selectedCount > 0 && !allSelectableSelected}
                                    onChange={toggleSelectAll}
                                    isDisabled={selectableConversations.length === 0}
                                />
                                <Text fontSize="sm" fontWeight="500">
                                    Select All
                                </Text>
                            </HStack>
                            <Badge variant="solid" colorScheme="blue">
                                {selectedCount} selected
                            </Badge>
                        </Flex>

                        {/* Bulk Actions */}
                        {selectedCount > 0 && (
                            <HStack spacing={2}>
                                <Button
                                    size="sm"
                                    colorScheme="red"
                                    variant="solid"
                                    leftIcon={<DeleteIcon />}
                                    onClick={() => setShowDeleteModal(true)}
                                    isLoading={isDeleting}
                                    loadingText="Deleting..."
                                >
                                    Delete ({selectedCount})
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={toggleSelectionMode}
                                >
                                    Cancel
                                </Button>
                            </HStack>
                        )}

                        {/* Warning if current conversation is selected */}
                        {isCurrentConversationSelected && (
                            <Text fontSize="xs" color="orange.600" fontStyle="italic">
                                ⚠️ Your current conversation is selected for deletion
                            </Text>
                        )}
                    </VStack>
                </Box>
            </Collapse>

            {/* Conversation Items */}
            {conversations.map((conv) => (
                <ConversationItemWrapper
                    key={conv.id}
                    conversation={conv}
                    onSelect={onSelect}
                    onDelete={onDelete}
                    onRename={onRename}
                    onMove={onMove}
                    workspaces={workspaces}
                    currentWorkspaceId={currentWorkspaceId}
                    isActive={conv.id === currentConversationId}
                    isSelectionMode={isSelectionMode}
                    isSelected={selectedConversations.has(conv.id)}
                    onToggleSelection={toggleConversationSelection}
                    onClose={onClose}
                />
            ))}

            {/* Show message if no selectable conversations */}
            {isSelectionMode && selectableConversations.length === 0 && (
                <Box p={3} textAlign="center">
                    <Text fontSize="sm" color="gray.500">
                        No saved conversations available for selection
                    </Text>
                </Box>
            )}
        </VStack>
    );

    const sidebarContent = (
        <Flex direction="column" h="full">

            {/* Logo Section */}
            <Box 
                p={3} 
                display="flex"
                flexDirection="column"
                alignItems="center"
            >
                <Box as="a" href="https://sldatatech.com" target="_blank" rel="noopener noreferrer">
                    <Image 
                        src={logo} 
                        alt="SL Data Tech"
                        mt={4}
                        h={8}
                        objectFit="contain" 
                    />
                </Box>
                <Image 
                    src={aisystems} 
                    alt="AI systems" 
                    h={4}
                    mt={2}
                    mb={1} 
                    objectFit="contain"
                />
            </Box>

            {/* Action Buttons */}
            <Box p={3} borderBottom="1px" borderColor="chat.sidebarBorder">
                <VStack spacing={2}>
                    <Button
                        leftIcon={<AddIcon />}
                        colorScheme="brand"
                        variant="solid"
                        w="full"
                        size="sm"
                        onClick={() => {
                            onNewConversation();
                            onClose();
                        }}
                    >
                        New Conversation
                    </Button>
                    
                    {selectableConversations.length > 0 && (
                        <Button
                            leftIcon={isSelectionMode ? <CloseIcon /> : <FaEdit />}
                            variant="outline"
                            w="full"
                            size="sm"
                            onClick={toggleSelectionMode}
                            colorScheme={isSelectionMode ? "gray" : "blue"}
                        >
                            {isSelectionMode ? 'Cancel Selection' : 'Select Multiple'}
                        </Button>
                    )}
                </VStack>
            </Box>

            {/* Conversations List */}
            <Box flex="1" overflowY="auto" p={2}>
                {conversationsList}
            </Box>
        </Flex>
    );

    return (
        <>
            {/* Mobile Trigger Button */}
            <IconButton
                icon={<HamburgerIcon />}
                aria-label="Open conversations"
                onClick={onOpen}
                position="fixed"
                top={4}
                left={4}
                zIndex={10}
                colorScheme="brand"
                variant="solid"
                display={{ base: 'flex', md: 'none' }}
            />

            {/* Mobile Drawer */}
            <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="sm">
                <DrawerOverlay />
                <DrawerContent bg="chat.sidebarBg" color="chat.sidebarText">
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth="1px" borderColor="chat.sidebarBorder">
                        Conversations
                    </DrawerHeader>
                    <DrawerBody p={0}>
                        {sidebarContent}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* Desktop Sidebar */}
            <Box
                display={{ base: 'none', md: 'block' }}
                w="280px"
                h="100vh"
                bg="chat.sidebarBg"
                color="chat.sidebarText"
                borderRight="1px"
                borderColor="chat.sidebarBorder"
                position="relative"
                flexShrink={0}
            >
                {sidebarContent}
            </Box>

            {/* Bulk Delete Confirmation Modal */}
            <BulkDeleteConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleBulkDelete}
                selectedConversations={selectedConversations}
                conversations={conversations}
                currentConversationId={currentConversationId}
                isDeleting={isDeleting}
            />
        </>
    );
};

// Wrapper component for individual conversation items
const ConversationItemWrapper = ({
    conversation,
    onSelect,
    onDelete,
    onRename,
    onMove,
    workspaces,
    currentWorkspaceId,
    isActive,
    isSelectionMode,
    isSelected,
    onToggleSelection,
    onClose
}) => {
    const handleItemClick = useCallback(() => {
        if (isSelectionMode && !conversation.isTemporary) {
            onToggleSelection(conversation.id);
        } else {
            onSelect(conversation.id);
            onClose();
        }
    }, [isSelectionMode, conversation.id, conversation.isTemporary, onToggleSelection, onSelect, onClose]);

    const selectionBg = useColorModeValue('blue.50', 'blue.900');
    const selectionBorder = useColorModeValue('blue.200', 'blue.700');

    if (isSelectionMode && !conversation.isTemporary) {
        // Selection mode for non-temporary conversations
        return (
            <Flex
                align="center"
                p={2}
                mx={1}
                borderRadius="md"
                bg={isSelected ? selectionBg : 'transparent'}
                border="2px solid"
                borderColor={isSelected ? selectionBorder : 'transparent'}
                _hover={{ bg: isSelected ? selectionBg : 'chat.sidebarHover' }}
                cursor="pointer"
                onClick={handleItemClick}
            >
                <Checkbox
                    isChecked={isSelected}
                    onChange={() => onToggleSelection(conversation.id)}
                    mr={3}
                />
                <Box flex="1" minW={0}>
                    <Text fontSize="sm" isTruncated>
                        {conversation.name}
                        {conversation.id === isActive && (
                            <Badge ml={2} size="sm" colorScheme="green">
                                Current
                            </Badge>
                        )}
                    </Text>
                </Box>
            </Flex>
        );
    }

    // Normal mode or temporary conversations
    return (
        <ConversationItem
            conversation={conversation}
            onSelect={onSelect}
            onDelete={onDelete}
            onRename={onRename}
            onMove={onMove}
            workspaces={workspaces}
            currentWorkspaceId={currentWorkspaceId}
            isActive={isActive}
            onClose={onClose}
        />
    );
};

export default ConversationList;